import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { BACKEND_URL, getQueryClient } from '@lib/config'
import axios from 'axios'
import { createTranslator } from 'use-intl/core'

import {
  setCookieAnonymousTokenServerAction,
  setCookieRefreshTokenServerAction,
  setCookieTokenServerAction,
} from '~/actions/set-cookie'
import { dictionaries } from '~/get-dictionaries'
import { toast } from '~/modules/common/components/ui/use-toast'

import { AUTH_ROUTES } from '../constants'
import { getRefreshToken } from '../services/api/user'

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig<unknown> {
  _retry?: boolean
}

const BASE_URL = BACKEND_URL
const isServer = typeof window === 'undefined'

const queryClient = getQueryClient()
async function cookiesInterceptor(req: InternalAxiosRequestConfig<unknown>) {
  let language = 'es'
  if (isServer) {
    const { cookies } = await import('next/headers')
    const cookieStore = await cookies()
    const allCookies = cookieStore.getAll()

    // Build cookie string for the request
    const cookiesString = allCookies
      .map(item => `${item.name}=${item.value}`)
      .join('; ')
    req.headers.cookie = cookiesString

    // Extract NEXT_LOCALE from cookies for Accept-Language header
    const localeCookie = allCookies.find(
      cookie => cookie.name === 'NEXT_LOCALE',
    )
    if (localeCookie) {
      language = localeCookie.value
    }
  }
  else {
    language
      = Object.fromEntries(
        new URLSearchParams(document.cookie.replace(/; /g, '&')),
      ).NEXT_LOCALE || language
  }
  req.headers['Accept-Language'] = language
  return req
}

const httpInstance = axios.create({
  baseURL: BASE_URL,
  responseType: 'json',
  withCredentials: true,
  timeout: 60000,
  paramsSerializer: { indexes: null },
})

httpInstance.interceptors.request.use(cookiesInterceptor)

httpInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    // console.log(error, 'error interceptor');
    // console.log(error.response, 'error response');
    const originalRequest = error.config as CustomAxiosRequestConfig
    const isRefreshRoute = originalRequest.url?.includes('/auth/refresh')
    const isUnauthorizedError = error.response?.status === 401
    const isServerError
      = error.response?.status === 500 || error.code === 'ERR_NETWORK'
    if (isServerError && document) {
      try {
        const language
          = Object.fromEntries(
            new URLSearchParams(document.cookie.replace(/; /g, '&')),
          ).NEXT_LOCALE || 'en'
        const messages = dictionaries[language as 'en' | 'es']
        const t = createTranslator({
          locale: language,
          messages: messages as IntlMessages,
        })
        toast({
          title: t('common.error'),
          variant: 'destructive',
        })
      }
      catch (err) {
        console.log(err, 'err')
      }
    }
    const isAuthRoute = AUTH_ROUTES.some(
      route => !isServer && window.location.pathname.includes(route),
    )
    const cleanup = async () => {
      try {
        await setCookieTokenServerAction('')
        await setCookieRefreshTokenServerAction('')
      }
      catch (err) {
        console.log(err, 'error cleaning up')
      }
      finally {
        window.location.href = '/login'
      }
    }
    if (isRefreshRoute && isUnauthorizedError) {
      console.log('isRefreshRoute && isUnauthorizedError')
      await cleanup()
      return Promise.reject(error)
    }
    if (
      isUnauthorizedError
      && !isRefreshRoute
      && !isAuthRoute
      && !originalRequest._retry
      && !isServer
    ) {
      originalRequest._retry = true
      try {
        const res = await getRefreshToken()
        if ('response' in res) {
          const axiosRes = res.response as AxiosResponse
          if (axiosRes.status === 401) {
            throw new Error('Unauthorized')
          }
        }
        if ('data' in res.data && 'accessToken' in res.data.data) {
          await setCookieTokenServerAction(res.data.data.accessToken)
          return httpInstance.request(originalRequest)
        }
      }
      catch (err) {
        console.log(err, 'err interceptor')
        try {
          await setCookieTokenServerAction('')
          await setCookieRefreshTokenServerAction('')
          await queryClient.resetQueries()
        }
        catch (err) {
          console.log(err)
        }
        finally {
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    }

    // Sesión caducada
    if (error.response?.status === 401) {
      const apiError = error.response.data as APIError
      if (apiError?.error?.errors?.[0]?.reason === 'WrongCredentials') {
        await setCookieTokenServerAction('')
        await setCookieAnonymousTokenServerAction('')
        await setCookieRefreshTokenServerAction('')
        await queryClient.resetQueries()
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  },
)

export { httpInstance }
